import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserRoles } from "./useUserRoles";

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRoles = [],
}) => {
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const { hasRole, roles } = useUserRoles();

  if (isLoading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <h1>Authenticating...</h1>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="auth-login-screen">
          <div className="auth-container">
            <h1>Entrustfi WhatsApp App</h1>
            <p>Please log in to access the system</p>
            <button
              className="auth-login-button"
              onClick={() => {
                // Auth0 SPA JS v2 cache key uses audience, not domain
                const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || "qfh9EWYN0tUhDIgRFkto4QR41Yet9Iz1";
                const audience = import.meta.env.VITE_AUTH0_API_AUDIENCE || "https://api.whatsapp.entrustfi.io";
                const cacheKey = `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`;
                localStorage.removeItem(cacheKey);
                loginWithRedirect({
                  authorizationParams: {
                    redirect_uri: window.location.origin,
                  },
                });
              }}
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has required roles
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => hasRole(role));

    if (!hasRequiredRole) {
      return (
        <div className="app">
          <div className="auth-error-screen">
            <div className="auth-container">
              <h1>Access Denied</h1>
              <p>You don't have permission to access this application.</p>
              <p>
                <strong>Your roles:</strong>{" "}
                {roles.length > 0 ? roles.join(", ") : "None"}
              </p>
              <p>
                <strong>Required roles:</strong> {requiredRoles.join(" or ")}
              </p>
              <div className="auth-actions">
                <button
                  className="auth-logout-button"
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
